import { createContext, ReactNode, useContext } from "react";

import {
  SalesforceChatSupport,
  SalesforceChatSupportContextValue,
  SalesforceConfig,
} from "./SalesforceChatSupport";

export const LoginSalesforceChatSupportContext = createContext<
  SalesforceChatSupportContextValue | undefined
>(undefined);

interface LoginSalesforceChatSupportProps {
  children: ReactNode;
  /**
   * Salesforce configuration for login support chat.
   * If not provided, defaults to environment variables
   * (NEXT_PUBLIC_SALESFORCE_LOGIN_SUPPORT_CHAT_*)
   */
  config?: SalesforceConfig;
}

/**
 * Salesforce chat support provider for login/pre-authentication pages.
 * This provider persists across navigation from login to authenticated pages.
 */
export const LoginSalesforceChatSupport = ({
  children,
  config,
}: LoginSalesforceChatSupportProps) => {
  const defaultConfig: SalesforceConfig = {
    key:
      config?.key ??
      process.env["NEXT_PUBLIC_SALESFORCE_LOGIN_SUPPORT_CHAT_KEY"] ??
      "",
    siteEndpoint:
      config?.siteEndpoint ??
      process.env["NEXT_PUBLIC_SALESFORCE_LOGIN_SUPPORT_CHAT_SITE_ENDPOINT"] ??
      "",
    scrt2Url:
      config?.scrt2Url ??
      process.env["NEXT_PUBLIC_SALESFORCE_LOGIN_SUPPORT_CHAT_SCRT2URL"] ??
      "",
    sessionLabel:
      config?.sessionLabel ?? "Pre_Authentication_Messaging_Session",
    websiteLabel: config?.websiteLabel ?? "Pre-Authentication Newsela",
    chatActiveKey: config?.chatActiveKey ?? "newsela_login_support_chat_active",
  };

  return (
    <SalesforceChatSupport
      context={LoginSalesforceChatSupportContext}
      config={defaultConfig}
    >
      {children}
    </SalesforceChatSupport>
  );
};

LoginSalesforceChatSupport.displayName = "LoginSalesforceChatSupport";

/**
 * Hook to access the login Salesforce chat support context.
 * Must be used within a LoginSalesforceChatSupport provider.
 */
export const useLoginSalesforceChatSupport =
  (): SalesforceChatSupportContextValue => {
    const context = useContext(LoginSalesforceChatSupportContext);

    if (context === undefined) {
      throw new Error(
        "useLoginSalesforceChatSupport must be used within a LoginSalesforceChatSupport provider",
      );
    }

    return context;
  };
